import { CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoSearchOutline } from "react-icons/io5";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apis from "services";
import { formatChatDate } from "utils/formatDate";

const statusLabel = (status) => ({
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
}[status] || status);

const SupportAttachmentPreview = ({ conversationId, item, onOpen }) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.attachment?.mimeType?.startsWith("image/");

  useEffect(() => {
    if (!isImage) return undefined;
    let active = true;
    let objectUrl = "";
    apis.getSupportAttachment(conversationId, item._id)
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        if (active) setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (active) setPreviewFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [conversationId, isImage, item._id]);

  return (
    <button type="button" className={`support-attachment ${isImage ? "support-image-attachment" : ""}`} onClick={() => onOpen(item)}>
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={item.attachment.originalName || "Support attachment"}
          onError={() => {
            setPreviewFailed(true);
            setPreviewUrl("");
          }}
        />
      ) : isImage ? (
        <span className="support-image-placeholder">
          {previewFailed ? "Image unavailable" : "Loading image…"}
        </span>
      ) : (
        <span>📎 {item.attachment.originalName}</span>
      )}
    </button>
  );
};

const TicketDetails = () => {
  const { lang, ticketId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [typingLabel, setTypingLabel] = useState("");
  const [conversations, setConversations] = useState([]);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [inboxFilters, setInboxFilters] = useState({ search: "", status: "all" });
  const typingTimer = useRef(null);
  const attachmentInputRef = useRef(null);
  const activeTicketRef = useRef(ticketId);
  const draft = drafts[ticketId] || { message: "", attachment: null };
  const message = draft.message || "";
  const attachment = draft.attachment || null;

  const updateDraft = useCallback((updates) => {
    setDrafts((current) => ({
      ...current,
      [ticketId]: { message: "", attachment: null, ...current[ticketId], ...updates },
    }));
  }, [ticketId]);

  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    try {
      const response = await apis.getSupportConversations({
        ...inboxFilters,
        page: 1,
        limit: 100,
      });
      const firstPage = response?.data?.data;
      const totalPages = Math.max(firstPage?.totalPages || 1, 1);
      const remainingPages = totalPages > 1
        ? await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) =>
          apis.getSupportConversations({
            ...inboxFilters,
            page: index + 2,
            limit: 100,
          })))
        : [];
      const allConversations = [
        ...(firstPage?.conversations || []),
        ...remainingPages.flatMap((page) => page?.data?.data?.conversations || []),
      ];
      const uniqueConversations = [...new Map(
        allConversations.map((item) => [item._id, item])
      ).values()].sort((a, b) =>
        new Date(b.lastMessageAt || b.updatedAt || 0)
        - new Date(a.lastMessageAt || a.updatedAt || 0));
      setConversations(uniqueConversations);
      setInboxTotal(firstPage?.total || uniqueConversations.length);
    } catch (error) {
      toast.error(error.message || "Could not load support conversations");
    } finally {
      setInboxLoading(false);
    }
  }, [inboxFilters]);

  useEffect(() => {
    let active = true;
    activeTicketRef.current = ticketId;
    setLoading(true);
    setTypingLabel("");
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";

    const loadConversation = async () => {
      try {
        const response = await apis.getSupportConversation(ticketId);
        if (active) setConversation(response?.data?.data || null);
        await apis.markSupportRead(ticketId);
      } catch (error) {
        if (active) toast.error(error.message || "Could not load the conversation");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadConversation();
    return () => {
      active = false;
    };
  }, [ticketId]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    const refreshOnFocus = () => loadInbox();
    window.addEventListener("focus", refreshOnFocus);
    return () => window.removeEventListener("focus", refreshOnFocus);
  }, [loadInbox]);

  useEffect(() => {
    const resync = async () => {
      loadInbox();
      try {
        const response = await apis.getSupportConversation(ticketId);
        if (activeTicketRef.current === ticketId) setConversation(response?.data?.data || null);
      } catch (_) {
        // The standard loader displays actionable request errors.
      }
    };
    window.addEventListener("support-chat-resync", resync);
    return () => window.removeEventListener("support-chat-resync", resync);
  }, [loadInbox, ticketId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setInboxFilters((current) => ({ ...current, search: searchInput.trim() }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const refresh = (event) => {
      if (event.detail?._id === ticketId) {
        if (event.detail.deleted) {
          setConversation(null);
          navigate(`/${lang}/admin/tickets/manage-all`, { replace: true });
          return;
        }
        setConversation(event.detail);
        apis.markSupportRead(ticketId).catch(() => {});
      }

      if (event.detail?._id) {
        setConversations((current) => {
          if (event.detail.deleted) {
            return current.filter((item) => item._id !== event.detail._id);
          }
          const normalizedSearch = inboxFilters.search.toLowerCase();
          const matchesStatus = inboxFilters.status === "all"
            || event.detail.status === inboxFilters.status;
          const matchesSearch = !normalizedSearch || [
            event.detail.reference,
            event.detail.subject,
            event.detail.userId?.name,
            event.detail.userId?.email,
          ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
          if (!matchesStatus || !matchesSearch) {
            return current.filter((item) => item._id !== event.detail._id);
          }
          const exists = current.some((item) => item._id === event.detail._id);
          const next = exists
            ? current.map((item) => item._id === event.detail._id
              ? { ...item, ...event.detail, messages: undefined }
              : item)
            : [event.detail, ...current];
          return next.sort((a, b) =>
            new Date(b.lastMessageAt || b.updatedAt) - new Date(a.lastMessageAt || a.updatedAt));
        });
      }
    };
    const typing = (event) => {
      if (event.detail?.conversationId === ticketId) {
        setTypingLabel(event.detail.isTyping ? `${event.detail.actorName || "Investor"} is typing…` : "");
      }
    };
    window.addEventListener("support-chat-updated", refresh);
    window.addEventListener("support-chat-typing", typing);
    return () => {
      window.removeEventListener("support-chat-updated", refresh);
      window.removeEventListener("support-chat-typing", typing);
    };
  }, [inboxFilters, lang, navigate, ticketId]);

  const emitTyping = (isTyping) => {
    window.dispatchEvent(new CustomEvent("support-chat-typing-send", {
      detail: { conversationId: ticketId, isTyping },
    }));
  };

  const handleMessageChange = (event) => {
    updateDraft({ message: event.target.value });
    emitTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => emitTyping(false), 900);
  };

  const messages = useMemo(() => conversation?.messages || [], [conversation]);

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!message.trim() && !attachment) return;
    setSending(true);
    emitTyping(false);
    try {
      const payload = new FormData();
      if (message.trim()) payload.append("message", message.trim());
      if (attachment) payload.append("attachment", attachment);
      payload.append("clientMessageId", `admin-${Date.now()}`);
      const response = await apis.sendSupportMessage(ticketId, payload);
      if (activeTicketRef.current === ticketId) {
        setConversation(response?.data?.data || conversation);
      }
      updateDraft({ message: "", attachment: null });
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    } catch (error) {
      toast.error(error.message || "Could not send the message");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      const response = await apis.updateSupportStatus(ticketId, status);
      if (activeTicketRef.current === ticketId) {
        setConversation(response?.data?.data || conversation);
      }
    } catch (error) {
      toast.error(error.message || "Could not update the status");
    }
  };

  const openAttachment = async (item) => {
    const mimeType = item.attachment?.mimeType || "application/octet-stream";
    const canPreview = mimeType.startsWith("image/") || mimeType === "application/pdf";
    const attachmentWindow = canPreview ? window.open("about:blank", "_blank") : null;
    if (attachmentWindow) {
      attachmentWindow.opener = null;
      attachmentWindow.document.title = "Opening attachment…";
      attachmentWindow.document.body.textContent = "Opening attachment…";
    }
    try {
      const response = await apis.getSupportAttachment(ticketId, item._id);
      const responseMimeType = response.data?.type || mimeType;
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: responseMimeType });
      const url = URL.createObjectURL(blob);
      if (canPreview && attachmentWindow) {
        attachmentWindow.location.replace(url);
      } else {
        attachmentWindow?.close();
        const link = document.createElement("a");
        link.href = url;
        link.download = item.attachment?.originalName || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
    } catch (error) {
      attachmentWindow?.close();
      toast.error(error.message || "Could not open the attachment");
    }
  };

  const isClosed = conversation?.status === "closed";

  return (
    <div className="support-workspace support-multichat-workspace">
      <aside className="support-chat-list-panel">
        <div className="support-chat-list-header">
          <div>
            <h2>Support inbox</h2>
            <span>{inboxTotal} conversations</span>
          </div>
          <Link to={`/${lang}/admin/tickets/manage-all`} aria-label="Open full support inbox">View all</Link>
        </div>
        <div className="support-chat-list-filters">
          <label className="support-chat-list-search">
            <IoSearchOutline />
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search chats"
            />
          </label>
          <select
            value={inboxFilters.status}
            onChange={(event) => setInboxFilters((current) => ({ ...current, status: event.target.value }))}
            aria-label="Filter conversations by status"
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="support-chat-list" aria-busy={inboxLoading}>
          {inboxLoading ? (
            <div className="support-chat-list-loading"><CircularProgress size={26} color="error" /></div>
          ) : conversations.length === 0 ? (
            <p className="support-chat-list-empty">No conversations found</p>
          ) : conversations.map((item) => (
            <Link
              key={item._id}
              to={`/${lang}/admin/tickets/${item._id}`}
              className={`support-chat-list-item ${item._id === ticketId ? "active" : ""} ${item.unreadByAdmin > 0 ? "unread" : ""}`}
            >
              <span className="support-chat-avatar" aria-hidden="true">
                {(item.userId?.name || "I").trim().charAt(0).toUpperCase()}
              </span>
              <span className="support-chat-list-copy">
                <span className="support-chat-list-row">
                  <strong>{item.userId?.name || "Investor"}</strong>
                  <time>{formatChatDate(item.lastMessageAt || item.updatedAt)}</time>
                </span>
                <span className="support-chat-list-subject">{item.subject || item.reference}</span>
                <span className="support-chat-list-row">
                  <small>{item.latestMessagePreview || "No messages yet"}</small>
                  {item.unreadByAdmin > 0 && <b>{item.unreadByAdmin}</b>}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </aside>

      <main className="support-chat-panel">
        {loading ? (
          <div className="content-loader"><CircularProgress color="error" /></div>
        ) : !conversation ? (
          <p className="no-record-found">Conversation not found</p>
        ) : <>
        <header className="support-chat-header">
          <div>
            <small>{conversation.reference}</small>
            <h2>{conversation.subject}</h2>
            <p>{conversation.userId?.name} · {conversation.userId?.email}</p>
          </div>
          <select value={conversation.status} onChange={(event) => changeStatus(event.target.value)}>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </header>

        <section className="support-message-stream">
          {messages.map((item) => (
            <article key={item._id} className={`support-message support-message-${item.senderType}`}>
              <small className="support-message-sender">
                {item.sender?.name || item.senderName || (item.senderType === "system" ? "JTC InvestIn" : conversation.userId?.name || "Investor")}
              </small>
              {item.body && <p>{item.body}</p>}
              {item.attachment && (
                <SupportAttachmentPreview conversationId={ticketId} item={item} onOpen={openAttachment} />
              )}
              <time>{formatChatDate(item.createdAt || item.deliveredAt)}</time>
            </article>
          ))}
          {typingLabel && <div className="support-typing-indicator">{typingLabel}</div>}
        </section>

        <form className="support-composer" onSubmit={sendMessage}>
          {isClosed && <p>This conversation is closed. Reopen it to send another message.</p>}
          <textarea
            value={message}
            onChange={handleMessageChange}
            placeholder="Write a reply…"
            rows={3}
            disabled={isClosed || sending}
          />
          <div>
            <input
              ref={attachmentInputRef}
              className="support-native-file-input"
              type="file"
              aria-label="Attach file"
              onChange={(event) => updateDraft({ attachment: event.target.files?.[0] || null })}
              disabled={isClosed || sending}
            />
            {attachment && (
              <span className="support-selected-file">
                {attachment.name}
                <button
                  type="button"
                  aria-label="Remove attachment"
                  onClick={() => {
                    updateDraft({ attachment: null });
                    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
                  }}
                >
                  ×
                </button>
              </span>
            )}
            <button type="submit" disabled={isClosed || sending || (!message.trim() && !attachment)}>{sending ? "Sending…" : "Send"}</button>
          </div>
        </form>
        </>}
      </main>

      <aside className="support-details-panel">
        {conversation ? <>
        <h3>Conversation details</h3>
        <dl>
          <dt>Status</dt><dd>{statusLabel(conversation.status)}</dd>
          <dt>Priority</dt><dd>{conversation.priority}</dd>
          <dt>Topic</dt><dd>{conversation.category}</dd>
          <dt>Opened</dt><dd>{formatChatDate(conversation.createdAt)}</dd>
          <dt>Unread</dt><dd>{conversation.unreadByAdmin || 0}</dd>
        </dl>
        {conversation.feedback && (
          <div className="support-feedback">
            <h4>Investor feedback</h4>
            <strong>{"★".repeat(conversation.feedback.rating)}</strong>
            <p>{conversation.feedback.comment || "No written comment"}</p>
          </div>
        )}
        </> : <p className="no-record-found">Select a conversation</p>}
      </aside>
    </div>
  );
};

export default TicketDetails;
